import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { appConfig } from 'src/config';
import { CampaignStats, CDRStatus } from './portal';
import { CampaignLogStatus } from './campaigns';
import { withSessionExpiredHandler } from 'src/api/common/baseQueryWithAuth';
import { fetchAllWhatsappTemplatesSynchronization } from 'src/api/meta/fetchAllWhatsappTemplatesSynchronization';

export enum TemplateButtonType {
  PHONE_NUMBER = 'PHONE_NUMBER',
  QUICK_REPLY = 'QUICK_REPLY',
  URL = 'URL',
  OTP = 'OTP',
  COPY_CODE = 'COPY_CODE',
}

export enum TemplateHeaderFileType {
  IMAGE = 'IMAGE',
  VIDEO = 'VIDEO',
  DOCUMENT = 'DOCUMENT',
}

export enum WhatsappTemplateStatus {
  NEW = 'NEW',
  APPROVED = 'APPROVED',
  PENDING = 'PENDING',
  REJECTED = 'REJECTED',
  'SENT FOR APPROVAL' = 'SENT FOR APPROVAL',
}

export enum WhatsappTemplateCategory {
  AUTHENTICATION = 'AUTHENTICATION',
  MARKETING = 'MARKETING',
  UTILITY = 'UTILITY',
}

export interface WhatsappTemplate {
  category: WhatsappTemplateCategory;
  language: string;
  name: string;
  status: WhatsappTemplateStatus;
  whatsAppMessageTemplateId: string;
}

export interface WhatsappTemplateDetails {
  id: string;
  status: WhatsappTemplateStatus;
  category: WhatsappTemplateCategory;
  language: string;
  name: string;
  components: {
    type: string;
    format?: TemplateHeaderFileType | 'TEXT';
    code_expiration_minutes?: number;
    add_security_recommendation?: boolean;
    text?: string;
    example?: { header_handle?: string[]; header_text?: string[]; body_text?: string[][] };
    buttons?: {
      otp_type?: string;
      text?: string;
      type: TemplateButtonType;
      phoneNumber?: string;
      url?: string;
      example?: string[];
    }[];
  }[];
}
export interface CreateWhatsappTemplatePayload {
  businessAccountId: string;
  message: Omit<WhatsappTemplateDetails, 'id' | 'status'>;
}

export interface WhatsappDefaultAccount {
  id: string;
  tenantId: string;
  businessAccountId: string;
  createdAt: string;
  updatedAt: string;
  isDisabled: boolean;
  channel: string;
  name: string;
  appId: string;
  metaAppId: string;
}

export enum WhatsappPricingPeriodCategory {
  ACTIVE = 'ACTIVE',
  PAST = 'PAST',
}

export type WhatsappTierCategory =
  | 'AUTHENTICATION'
  | 'AUTHENTICATION_INTERNATIONAL'
  | 'UTILITY'
  | 'MARKETING'
  | 'SERVICE';

export interface WhatsappVolumeTierPrice {
  name?: string;
  category?: WhatsappTierCategory;
  messageCategory?: WhatsappTierCategory;
  lower?: number;
  upper?: number | null;
  useUpperMax?: boolean;
  price?: number;
}

export interface WhatsappPricing {
  authInternationalPrice: number;
  authenticationPrice: number;
  country: { callingCode: number };
  countryName: string;
  id: number;
  isBlocked: boolean;
  marketingPrice: number;
  periodCategory: WhatsappPricingPeriodCategory;
  servicePrice: number;
  startDate: string;
  updateReason: string;
  utilityPrice: number;
  volumeTiersPriceDtoList?: WhatsappVolumeTierPrice[];
  tiers?: Partial<Record<WhatsappTierCategory, WhatsappVolumeTierPrice[]>>;
  authenticationTieredPricing?: WhatsappVolumeTierPrice[];
  authInternationalTieredPricing?: WhatsappVolumeTierPrice[];
  utilityTieredPricing?: WhatsappVolumeTierPrice[];
  tieredPricingEnabled?: boolean;
}

export interface WhatsappBalanceDetailsDailyEntry {
  countries: Record<
    string,
    {
      tiers: {};
      totalCostTiers: number;
      defaultCategoriesTotalCostPerCountry: number;
      defaultCategories: Record<
        'AUTHENTICATION' | 'UTILITY' | 'MARKETING' | 'SERVICE',
        | {
          amount: number;
          unitCost: number;
          totalCost: number;
        }
        | undefined
      >;
    }
  >;
  totalCostPerDay: number;
  utility: number;
  authentication: number;
  authenticationInternational: number;
  marketing: number;
  service: number;
}

export type WhatsappBalanceDetailsDaily = Record<string, WhatsappBalanceDetailsDailyEntry>;

export interface DialCode {
  countryCode: string;
  countryName: string;
  dialCode: string;
}

export interface CDR {
  account: null | string;
  createDateTime: string;
  createDateTimestamp: number;
  destinationName: string;
  lastStatus: CDRStatus;
  lastStatusTime: string;
  lastStatusTimestamp: number;
  receiver: string;
  sender: string;
  statusDetails?: string;
}

export interface WhatsappCDRFilters {
  senderIds: string[];
  receivers?: string[];
  destinations: string[];
  accounts: string[];
  lastStatuses?: CDRStatus[];
}

export interface WhatsappCDRResponse {
  content: CDR[];
  page?: {
    number: number;
    size: number;
    totalElements: number;
    totalPages: number;
  };
  totalElements?: number;
  totalPages?: number;
  number?: number;
  size?: number;
  numberOfElements?: number;
  last?: boolean;
  first?: boolean;
  empty?: boolean;
}

export interface ContactWithCampaign {
  campaignId: number;
  campaignName: string;
  contactId: number;
  destination: string;
  foundInContacts: boolean;
  id: string;
  name: string;
  number: string;
}

export interface CampaignLog {
  createdAt: string;
  createdAtTimestamp: number;
  direction: string;
  id: string;
  message: { templateName: string; languageType: string; businessAccountId: string };
  metaMessageId: string;
  phoneNumber: string;
  status: CampaignLogStatus;
  fallbackStatus?: CampaignLogStatus;
  fallbackStatusDetails?: string;
  type: string;
  updatedAt: string;
  updatedAtTimestamp: number;
  statusDetails: string;
}

export interface CampaignLogsResponse {
  content: CampaignLog[];
  page?: {
    number: number;
    size: number;
    totalElements: number;
    totalPages: number;
  };
  totalElements?: number;
  totalPages?: number;
  number?: number;
  size?: number;
  numberOfElements?: number;
  last?: boolean;
  first?: boolean;
  empty?: boolean;
}

export interface ContactsWithoutPricing {
  phoneNumbers: number;
  countries: string[];
}

export type MessagesStats = Record<
  string,
  {
    freeMessageCount: number;
    messageCount: number;
    paidMessageCount: number;
  }
>;

export interface AdminWhatsappBalanceOverview {
  totalUsage: number;
  dailyStats: Record<string, number>;
}

export type WhatsappInvoiceStatus =
  | 'DRAFT'
  | 'OPEN'
  | 'PAID'
  | 'UNPAID'
  | 'OVERDUE'
  | 'IN_PROGRESS'
  | 'ERROR'
  | 'SENT'
  | 'NOT_SENT'
  | 'DELETED';

export interface WhatsappInvoice {
  invoiceNumber: string;
  amount: number;
  currency: string;
  status: WhatsappInvoiceStatus;
  customerId?: string;
  customerName: string;
  issueDate: string;
  saleDate?: string;
  dueDate: string;
  periodFrom: string;
  periodTo: string;
  deleted: boolean;
  errors?: string;
  pdfUrl?: string;
  excelUrl?: string;
}

export interface WhatsappInvoicesPage {
  content: WhatsappInvoice[];
  page: { number: number; size: number; totalElements: number; totalPages: number };
  last: boolean;
}

export const whatsappApi = createApi({
  tagTypes: [
    'WhatsappTemplate',
    'WhatsappTemplateDetails',
    'WhatsappDefaultAccount',
    'WhatsappPricing',
    'WhatsappBalanceDetailsDaily',
    'DialCode',
    'CDR',
    'ContactWithCampaign',
    'ContactsWithoutPricing',
    'MessagesStats',
    'WhatsappCDRFilters',
    'WhatsappInvoice',
  ],
  baseQuery: withSessionExpiredHandler(fetchBaseQuery({
    baseUrl: `${appConfig.META_URL}`,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('chatToken');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  })),
  reducerPath: 'whatsappApi',
  endpoints: (builder) => ({
    getDefaultAccount: builder.query<WhatsappDefaultAccount, void>({
      query: () => ({
        url: 'tenants/accounts/default',
      }),
      providesTags: [{ type: 'WhatsappDefaultAccount', id: 'defaultAccount' }],
    }),
    getTemplates: builder.query<WhatsappTemplate[], string>({
      async queryFn(businessAccountId, _api, _extraOptions, baseQuery) {
        if (!businessAccountId) {
          return { data: [] };
        }
        const basePath = `whatsapp/accounts/${businessAccountId}/templates/synchronization`;
        try {
          const rows = await fetchAllWhatsappTemplatesSynchronization(basePath, async (path) => {
            const res = await baseQuery({ url: path });
            if (res.error) {
              throw res.error;
            }
            return res.data as unknown;
          });
          return { data: rows as WhatsappTemplate[] };
        } catch (error) {
          return { error: error as FetchBaseQueryError };
        }
      },
      providesTags: [{ type: 'WhatsappTemplate', id: 'templates' }],
    }),
    createTemplate: builder.mutation<WhatsappTemplate, CreateWhatsappTemplatePayload>({
      query: (payload) => ({
        url: 'templates',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['WhatsappTemplate'],
    }),
    getTemplate: builder.query<WhatsappTemplateDetails, { businessAccountId: string; id: string }>({
      query: ({ businessAccountId, id }) => ({
        url: `whatsapp/accounts/${businessAccountId}/templates/${id}`,
      }),
      providesTags: [{ type: 'WhatsappTemplateDetails', id: 'template' }],
    }),
    deleteTemplate: builder.mutation<void, { id: string; name: string }>({
      query: ({ id, name }) => ({
        url: `templates/${id}/name/${name}`,
        method: 'DELETE',
        responseHandler: 'text',
      }),
      invalidatesTags: ['WhatsappTemplate'],
    }),
    uploadMedia: builder.query<{ fileId: string }, { formData: FormData; businessAccountId: string }>({
      query: ({ formData, businessAccountId }) => ({
        url: `${businessAccountId}/upload/media`,
        method: 'POST',
        body: formData,
        // Large files / slow backend can take time; avoid client abort before server responds
        timeout: 120_000,
      }),
    }),
    getPricing: builder.query<WhatsappPricing[], string>({
      query: (businessAccountId) => ({
        url: `pricing/${businessAccountId}`,
      }),
      providesTags: [{ type: 'WhatsappPricing', id: 'pricing' }],
    }),
    getUserBalanceOverview: builder.query<
      WhatsappBalanceDetailsDaily,
      { id: string; dateFrom: string; dateTo: string }
    >({
      query: ({ id, dateFrom, dateTo }) => ({
        url: `${id}/statistics/messages/user-balance-overview`,
        params: {
          from: dateFrom,
          to: dateTo,
        },
      }),
      providesTags: [{ type: 'WhatsappBalanceDetailsDaily', id: 'balanceOverview' }],
      transformResponse: (response: { dailyStats: WhatsappBalanceDetailsDaily }) => response.dailyStats,
    }),
    getDialCodes: builder.query<DialCode[], void>({
      query: () => ({
        url: 'dial-code',
      }),
      providesTags: [{ type: 'DialCode', id: 'dialCodes' }],
    }),
    getCDRs: builder.query<
      WhatsappCDRResponse,
      {
        id: string;
        page: number;
        size: number;
        sort: string;
        full?: boolean;
        senderIds?: string[];
        receiverIds?: string[];
        destinations?: string[];
        accounts?: string[];
        statuses?: CDRStatus[];
      }
    >({
      query: ({ id, senderIds, receiverIds, destinations, accounts, statuses, ...rest }) => {
        const searchParams = new URLSearchParams();
        (Object.entries(rest) as [string, string | number | boolean | undefined][]).forEach(
          ([key, value]) => {
            if (value !== undefined && value !== '') searchParams.set(key, String(value));
          }
        );
        if (senderIds?.length) searchParams.set('senderIds', senderIds.join(','));
        if (receiverIds?.length) searchParams.set('receiverIds', receiverIds.join(','));
        if (destinations?.length) searchParams.set('destinations', destinations.join(','));
        if (accounts?.length) searchParams.set('accounts', accounts.join(','));
        statuses?.forEach((s) => searchParams.append('statuses', s));
        const query = searchParams.toString();
        return { url: `../v2/api/cdr/${id}${query ? `?${query}` : ''}` };
      },
      transformResponse: (response: unknown) => {
        const raw = response as Record<string, unknown>;
        const content = Array.isArray(raw?.content) ? raw.content : [];
        const number = (raw?.number as number) ?? 0;
        const size = (raw?.size as number) ?? 20;
        const numberOfElements = (raw?.numberOfElements as number) ?? content.length;
        const last = raw?.last === true;
        const pageObj = raw?.page as Record<string, unknown> | undefined;
        const totalElementsFromApi =
          (raw?.totalElements as number) ?? (pageObj?.totalElements as number);
        const totalElements =
          typeof totalElementsFromApi === 'number'
            ? totalElementsFromApi
            : last
              ? number * size + numberOfElements
              : (number + 2) * size;
        const totalPagesFromApi = (raw?.totalPages as number) ?? (pageObj?.totalPages as number);
        const totalPages =
          typeof totalPagesFromApi === 'number'
            ? totalPagesFromApi
            : last
              ? number + 1
              : number + 2;
        return {
          content,
          page: {
            number,
            size,
            totalElements,
            totalPages,
          },
        };
      },
      providesTags: [{ type: 'CDR', id: 'cdrs' }],
    }),
    getCDRFilters: builder.query<WhatsappCDRFilters, string>({
      query: (tenantId) => {
        return {
          url: tenantId ? `../v2/api/cdr/${tenantId}/filters` : '../v2/api/cdr/filters',
        };
      },
      transformResponse: (response: unknown): WhatsappCDRFilters => {
        const raw = response as Record<string, unknown>;
        const arr = (v: unknown): string[] =>
          Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
        const lastStatusesRaw =
          raw?.lastStatuses ?? (raw?.last_statuses as string[] | undefined);
        return {
          senderIds: arr(raw?.senderIds ?? raw?.sender_ids),
          receivers: arr(raw?.receivers),
          destinations: arr(raw?.destinations),
          accounts: arr(raw?.accounts),
          lastStatuses: Array.isArray(lastStatusesRaw)
            ? lastStatusesRaw.filter((x): x is CDRStatus => typeof x === 'string')
            : [],
        };
      },
      providesTags: [{ type: 'WhatsappCDRFilters', id: 'cdrFilters' }],
    }),
    getContactsWithCampaign: builder.query<ContactWithCampaign[], string>({
      query: (id) => ({
        url: `phone_number/${id}/targets/campaigned`,
        params: {
          page: 0,
          size: 1000000,
        },
      }),
      providesTags: [{ type: 'ContactWithCampaign', id: 'contactsWithCampaign' }],
      transformResponse: (response: { content: ContactWithCampaign[] }) => response.content,
    }),
    getCampaignStats: builder.query<CampaignStats, number>({
      query: (campaignId) => ({
        url: `campaigns/${campaignId}/statistics`,
      }),
    }),
    getCampaignLogs: builder.query<
      CampaignLogsResponse,
      {
        page: number;
        size: number;
        sort: string;
        campaignId: number;
        hasFallback?: boolean;
      }
    >({
      query: ({ campaignId, hasFallback, ...params }) => ({
        url: `campaigns/${campaignId}/logs${hasFallback ? '/fallback' : ''}`,
        params,
      }),
      transformResponse: (response: unknown): CampaignLogsResponse => {
        const raw = response as Record<string, unknown>;
        const content = Array.isArray(raw?.content) ? (raw.content as CampaignLog[]) : [];
        const number = (raw?.number as number) ?? 0;
        const size = (raw?.size as number) ?? 20;
        const numberOfElements = (raw?.numberOfElements as number) ?? content.length;
        const last = raw?.last === true;
        const pageObj = raw?.page as Record<string, unknown> | undefined;
        const totalElementsFromApi = (raw?.totalElements as number) ?? (pageObj?.totalElements as number);
        const totalElements =
          typeof totalElementsFromApi === 'number'
            ? totalElementsFromApi
            : last
              ? number * size + numberOfElements
              : (number + 2) * size;
        const totalPagesFromApi = (raw?.totalPages as number) ?? (pageObj?.totalPages as number);
        const totalPages =
          typeof totalPagesFromApi === 'number'
            ? totalPagesFromApi
            : last
              ? number + 1
              : number + 2;

        return {
          content,
          page: {
            number,
            size,
            totalElements,
            totalPages,
          },
          totalElements,
          totalPages,
          number,
          size,
          numberOfElements,
          last,
          first: raw?.first === true,
          empty: raw?.empty === true,
        };
      },
    }),
    getContactsWithoutPricing: builder.query<
      ContactsWithoutPricing,
      { businessAccountId: string; contacts: { mobilePhone: string }[] }
    >({
      query: ({ businessAccountId, contacts }) => ({
        url: `pricing/${businessAccountId}/verification`,
        method: 'POST',
        body: { contacts },
      }),
      providesTags: [{ type: 'ContactsWithoutPricing', id: 'contactsWithoutPricing' }],
    }),
    getMessagesStatsYearly: builder.query<MessagesStats, string>({
      query: (businessAccountId) => ({
        url: 'statistics/messages/year/current',
        params: { businessAccountId },
      }),
      providesTags: [{ type: 'MessagesStats', id: 'messagesStatsYearly' }],
      transformResponse: (response: { messagingStatistics: MessagesStats }) => response.messagingStatistics,
    }),
    getMessagesStatsMonthly: builder.query<MessagesStats, string>({
      query: (businessAccountId) => ({
        url: 'statistics/messages/month/current',
        params: { businessAccountId },
      }),
      providesTags: [{ type: 'MessagesStats', id: 'messagesStatsMonthly' }],
      transformResponse: (response: { messagesStatistics: MessagesStats }) => response.messagesStatistics,
    }),
    getAdminMessagesStatsYearly: builder.query<MessagesStats, void>({
      query: () => ({
        url: 'statistics/admin/messages/year/current',
      }),
      providesTags: [{ type: 'MessagesStats', id: 'adminMessagesStatsYearly' }],
      transformResponse: (response: { messagingStatistics: MessagesStats }) => response.messagingStatistics ?? {},
    }),
    getAdminBalanceOverview: builder.query<AdminWhatsappBalanceOverview, { from: string; to: string; appId: string }>({
      query: ({ from, to, appId }) => ({
        url: 'statistics/user-balance-overview',
        params: { from, to, appId },
      }),
      providesTags: [{ type: 'MessagesStats', id: 'adminBalanceOverview' }],
    }),
    getInvoices: builder.query<
      WhatsappInvoicesPage,
      { businessAccountId: string; page: number; size: number; sort: string }
    >({
      query: ({ businessAccountId, page, size, sort }) => ({
        url: `invoices/${businessAccountId}`,
        params: { page, size, sort },
      }),
      providesTags: [{ type: 'WhatsappInvoice', id: 'list' }],
      transformResponse: (response: unknown) => {
        const raw = response as Record<string, unknown>;
        const content = (Array.isArray(raw?.content) ? raw.content : []) as WhatsappInvoice[];
        const number = (raw?.number as number) ?? 0;
        const size = (raw?.size as number) ?? 20;
        const numberOfElements = (raw?.numberOfElements as number) ?? content.length;
        const last = raw?.last === true;
        const totalElements =
          typeof raw?.totalElements === 'number'
            ? (raw.totalElements as number)
            : last
              ? number * size + numberOfElements
              : (number + 2) * size;

        return {
          content,
          last,
          page: {
            number,
            size,
            totalElements,
            totalPages: typeof raw?.totalPages === 'number' ? (raw.totalPages as number) : last ? number + 1 : number + 2,
          },
        };
      },
    }),
  }),
});

export const {
  useGetTemplatesQuery,
  useCreateTemplateMutation,
  useLazyUploadMediaQuery,
  useDeleteTemplateMutation,
  useGetTemplateQuery,
  useGetDefaultAccountQuery,
  useGetPricingQuery,
  useGetUserBalanceOverviewQuery,
  useGetDialCodesQuery,
  useLazyGetCDRsQuery,
  useGetCDRFiltersQuery,
  useLazyGetContactsWithCampaignQuery,
  useGetCampaignStatsQuery,
  useLazyGetCampaignLogsQuery,
  useLazyGetContactsWithoutPricingQuery,
  useGetMessagesStatsYearlyQuery,
  useGetMessagesStatsMonthlyQuery,
  useGetAdminMessagesStatsYearlyQuery,
  useGetAdminBalanceOverviewQuery,
  useGetInvoicesQuery,
  useLazyGetInvoicesQuery,
} = whatsappApi;
